import React, { useState } from "react";

import { Card, CardTitle, Icon } from "./ui";
import useWorkSummary from "../../../hooks/useWorkSummary";

/*
  오늘 작업 진행.

  두 가지를 같이 보여준다.
    · 전체 공정   — 오늘 잡힌 작업 전체가 얼마나 끝났나
    · 작업자별    — 누가 몇 건 중 몇 건을 끝냈나

  전체만 보면 «누가 밀리는지»를 모르고, 작업자별만 보면 «오늘 다 끝나는지»를 모른다.
  현장에서는 "18/24" 처럼 건수로 말하므로 % 와 건수를 같이 적는다.

  데이터: /api/safety-report/work-summary (counts, byVehicle). 집계는 서버가 한다.
*/

function pct(done, total) {
  const t = Number(total || 0);
  return t > 0 ? Math.round((Number(done || 0) / t) * 100) : 0;
}

/*
  오른쪽 위 «오늘 작업» 카드.

  자리 배분 규칙
    · 미조치 위반이 있으면(compact) 작업자 목록을 접고 «요약 한 줄»만 남긴다.
      급한 건 위반이다 — 작업 진행표가 자리를 차지하고 있으면 안 된다.
    · 위반이 없으면 작업자별 목록을 펼치고, 인원이 많으면 카드 안에서만 스크롤한다
      (카드가 아래로 계속 자라서 대기열을 밀어내지 않게 최대 높이를 둔다).
    · 접혀 있어도 화살표를 누르면 언제든 펼칠 수 있다.
*/
export default function WorkProgress({ enabled = true, refreshMs = 60000, compact = false }) {
  const { data, failed } = useWorkSummary({ enabled, refreshMs });
  const [openList, setOpenList] = useState(false);

  const c = data?.counts;
  const total = Number(c?.total || 0);

  // 아직 못 받았거나 조회가 실패하면 자리만 비워 둔다(엉뚱한 0% 를 띄우지 않는다).
  if (failed || !c) return null;

  /*
    오늘 잡힌 작업이 없을 때 카드를 통째로 숨겼더니 «진행률이 어디 갔냐»가 됐다.
    숨기면 고장인지 일정이 없는 건지 구분이 안 된다 — 카드는 두고 사유를 적는다.
  */
  if (total === 0) {
    return (
      <Card className="shrink-0 px-4 py-3.5">
        <div className="flex items-baseline">
          <span className="text-[13px] font-semibold text-slate-900 dark:text-white">
            오늘 작업
          </span>
          <span className="ml-auto text-[12.5px] text-slate-400">0건</span>
        </div>
        <div className="mt-1.5 text-[12px] text-slate-400">
          오늘 예정된 작업이 없습니다
        </div>
      </Card>
    );
  }

  const done = Number(c.done || 0);
  const working = Number(c.working || 0);
  const waiting = Math.max(0, total - done - working);
  const overall = pct(done, total);

  const donePct = (done / total) * 100;
  const workPct = (working / total) * 100;

  const rows = (Array.isArray(data?.byVehicle) ? data.byVehicle : []).filter(
    (r) => Number(r?.total || 0) > 0
  );

  // 위반이 있으면 기본으로 접는다. 사용자가 화살표로 편 경우는 그 뜻을 존중한다.
  const showList = rows.length > 0 && (!compact || openList);

  return (
    <Card
      className={`flex shrink-0 flex-col overflow-hidden ${
        showList ? "max-h-[42%]" : ""
      }`}
    >
      <CardTitle
        title="오늘 작업"
        right={
          <span className="flex items-center gap-2 text-[12.5px] tabular-nums">
            <b className="font-bold text-slate-900 dark:text-white">{done}</b>
            <span className="text-slate-400"> / {total}건</span>
            <b className="ml-1 font-semibold text-emerald-600 dark:text-emerald-400">
              {overall}%
            </b>
            {rows.length > 0 && (
              <button
                type="button"
                onClick={() => setOpenList((v) => !v)}
                className={`flex h-5 w-5 items-center justify-center rounded text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/10 ${
                  showList ? "rotate-180" : ""
                }`}
                aria-label="작업자별 보기"
              >
                <Icon.Down size={13} />
              </button>
            )}
          </span>
        }
      />

      {/* 전체 공정 */}
      <div className="shrink-0 px-4 pb-3 pt-3.5">
        <div className="flex h-[9px] w-full overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
          <span className="h-full bg-emerald-500" style={{ width: `${donePct}%` }} />
          <span className="h-full bg-blue-500" style={{ width: `${workPct}%` }} />
        </div>

        <div className="mt-2.5 flex items-center gap-4 text-[11.5px]">
          <span className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
            <span className="h-[7px] w-[7px] rounded-sm bg-emerald-500" />
            완료{" "}
            <b className="font-semibold text-slate-900 tabular-nums dark:text-white">{done}</b>
          </span>
          <span className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
            <span className="h-[7px] w-[7px] rounded-sm bg-blue-500" />
            작업 중{" "}
            <b className="font-semibold text-slate-900 tabular-nums dark:text-white">{working}</b>
          </span>
          <span className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
            <span className="h-[7px] w-[7px] rounded-sm bg-slate-300 dark:bg-white/20" />
            대기{" "}
            <b className="font-semibold text-slate-900 tabular-nums dark:text-white">{waiting}</b>
          </span>
        </div>
      </div>

      {/* 작업자별 */}
      {showList && (
        <div className="min-h-0 flex-1 overflow-y-auto border-t border-slate-200 px-4 py-2.5 dark:border-white/10">
          <div className="flex flex-col gap-2.5">
            {rows.map((r, i) => {
              const p = pct(r.done, r.total);
              const label = String(r.worker || "").trim() || String(r.vehicle || "");
              const sub = r.worker ? String(r.vehicle || "") : "";

              /*
                상태 표시가 없으면 이 목록이 «송출 중인 카메라»처럼 보인다(실제로 그렇게 읽혔다).
                작업 일정이지 카메라 상태가 아님을 한 글자로 분명히 한다.
              */
              const working = Number(r.working || 0);
              const state =
                r.done >= r.total
                  ? { text: "완료", cls: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300" }
                  : working > 0
                  ? { text: "작업 중", cls: "bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300" }
                  : { text: "대기", cls: "bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-400" };

              return (
                <div key={`${r.vehicle}-${i}`}>
                  <div className="flex items-center gap-2 text-[12px]">
                    <span className="truncate font-semibold text-slate-800 dark:text-slate-100">
                      {label}
                    </span>
                    <span
                      className={`shrink-0 rounded px-1.5 py-[1px] text-[10.5px] font-semibold ${state.cls}`}
                    >
                      {state.text}
                    </span>
                    {sub && (
                      <span className="truncate text-[11px] text-slate-400">{sub}</span>
                    )}
                    <span className="ml-auto shrink-0 tabular-nums text-slate-500 dark:text-slate-400">
                      <b
                        className={`font-semibold ${
                          p === 100
                            ? "text-emerald-600 dark:text-emerald-400"
                            : "text-slate-900 dark:text-white"
                        }`}
                      >
                        {r.done}
                      </b>
                      /{r.total}
                    </span>
                  </div>
                  <div className="mt-1 h-[5px] overflow-hidden rounded-full bg-slate-100 dark:bg-white/10">
                    <span
                      className={`block h-full rounded-full ${
                        p === 100 ? "bg-emerald-500" : "bg-blue-500"
                      }`}
                      style={{ width: `${p}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
}
